/**
 * TwoFive PDP — minimal JS (Liquid-pure architecture)
 *
 * The variant picker classification is done in Liquid (we patched
 * snippets/main-product__variant-picker.liquid to add data-option-name and
 * tf-size-fieldset class, plus skip the native Color option when the
 * metafield custom.color_variants is set).
 *
 * The only thing that genuinely requires JS is wiring the personalization
 * checkbox so the line-item-property text input is `disabled` (and therefore
 * not submitted) when the customer hasn't opted in.
 *
 * If you want a 100% no-JS personalization toggle, the input always submits
 * and the merchant ignores empty values on the order side — let me know.
 */

(function () {
  'use strict';

  function wirePersonalization() {
    document.querySelectorAll('.tf-personalization').forEach(function (root) {
      if (root.dataset.tfWired === '1') return;
      root.dataset.tfWired = '1';

      var checkbox = root.querySelector('[data-tf-personalize-toggle]');
      var field = root.querySelector('[data-tf-personalize-field]');
      if (!checkbox || !field) return;

      function sync() {
        if (checkbox.checked) {
          field.disabled = false;
          setTimeout(function () { try { field.focus(); } catch (e) {} }, 50);
        } else {
          field.disabled = true;
          field.value = '';
        }
      }
      checkbox.addEventListener('change', sync);
      sync();
    });
  }

  /**
   * TALLA OBLIGATORIA — el cliente debe elegir una talla explícitamente.
   *
   * Shopify siempre pre-resuelve una variante por defecto (normalmente la
   * primera talla disponible) para poder pintar precio/imagen/SKU al cargar
   * la página, y esa talla llega marcada como `checked` en el HTML. En vez
   * de pelear contra ese comportamiento nativo (lo cual rompería el resto
   * del picker de variantes), dejamos que Shopify siga resolviendo esa
   * variante "por defecto" tal cual, y controlamos aparte:
   *
   *   1) El aspecto visual: el naranja de "seleccionada" solo se aplica si
   *      el fieldset tiene el atributo [data-tf-confirmed] (ver CSS).
   *   2) El botón "Añadir al carrito": se fuerza a disabled hasta que el
   *      cliente interactúe con el selector de talla.
   *   3) El envío del formulario: se bloquea en fase de captura si, por lo
   *      que sea (teclado, etc.), se intenta enviar sin haber confirmado.
   *
   * `data-tf-confirmed` se marca solo cuando el cliente dispara un evento
   * `change` real sobre un input de talla. Ese estado se guarda en
   * TF_SIZE_STATE (por section id) porque Shopify vuelve a renderizar por
   * AJAX el bloque del picker cada vez que cambia cualquier opción (p. ej.
   * color), y el nuevo HTML pierde cualquier atributo que hubiéramos puesto
   * a mano.
   */
  var TF_SIZE_STATE = {};

  /**
   * Si el fieldset de talla solo tiene UNA opción realmente seleccionable
   * (sin contar las agotadas/disabled), no hay ambigüedad posible — no
   * tiene sentido exigir un clic que además, técnicamente, nunca podría
   * llegar a producirse: un radio que ya viene marcado por Shopify (por
   * ser la única opción) no dispara `change` al volver a pulsarlo. En
   * ese caso damos la talla por confirmada automáticamente.
   */
  function tfSizeHasSingleAvailableOption(fieldset) {
    var enabled = fieldset.querySelectorAll('.product-option__input:not(:disabled)');
    return enabled.length === 1;
  }

  function tfSizeApplyState(fieldset, confirmed) {
    fieldset.toggleAttribute('data-tf-confirmed', !!confirmed);

    var msg = fieldset.querySelector('[data-tf-size-message]');
    if (msg) {
      msg.textContent = confirmed
        ? (msg.getAttribute('data-selected-text') || msg.textContent)
        : (msg.getAttribute('data-select-text') || msg.textContent);
    }

    if (confirmed) {
      var error = fieldset.nextElementSibling;
      if (error && error.hasAttribute('data-tf-size-error')) {
        error.hidden = true;
      }
    }

    tfSizeUpdateSubmitButton(fieldset, confirmed);
  }

  function tfSizeUpdateSubmitButton(fieldset, confirmed) {
    var sectionId = fieldset.getAttribute('data-tf-section-id');
    if (!sectionId) return;

    var button = document.getElementById('ProductSubmitButton-' + sectionId);
    if (!button) return;

    var textSpan = button.querySelector('span');

    if (!confirmed) {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('data-tf-size-lock', '1');
      if (textSpan) {
        if (!button.dataset.tfOriginalText) {
          button.dataset.tfOriginalText = textSpan.textContent;
        }
        var promptEl = fieldset.querySelector('[data-tf-size-message]');
        textSpan.textContent = promptEl
          ? promptEl.getAttribute('data-select-text')
          : textSpan.textContent;
      }
    } else if (button.getAttribute('data-tf-size-lock') === '1') {
      button.removeAttribute('data-tf-size-lock');
      button.removeAttribute('disabled');
      if (textSpan && button.dataset.tfOriginalText) {
        textSpan.textContent = button.dataset.tfOriginalText;
      }
      // A partir de aquí, si la combinación de opciones resulta agotada,
      // el propio product-variants.js de Shopify vuelve a deshabilitar el
      // botón con el texto "Agotado" tras su siguiente render — no hace
      // falta que nosotros lo gestionemos.
    }
  }

  function tfSizeInit(scope) {
    var root = scope || document;
    root.querySelectorAll('.tf-size-fieldset[data-tf-section-id]').forEach(function (fieldset) {
      var sectionId = fieldset.getAttribute('data-tf-section-id');
      if (tfSizeHasSingleAvailableOption(fieldset)) {
        TF_SIZE_STATE[sectionId] = true;
      }
      tfSizeApplyState(fieldset, !!TF_SIZE_STATE[sectionId]);
    });
  }

  function tfSizeWireEvents() {
    if (document.__tfSizeWired) return;
    document.__tfSizeWired = true;

    // 1) El cliente elige una talla → confirmamos y recordamos el estado.
    document.addEventListener('change', function (e) {
      var input = e.target.closest && e.target.closest('.tf-size-fieldset .product-option__input');
      if (!input) return;

      var fieldset = input.closest('.tf-size-fieldset');
      if (!fieldset) return;

      var sectionId = fieldset.getAttribute('data-tf-section-id');
      if (sectionId) TF_SIZE_STATE[sectionId] = true;

      tfSizeApplyState(fieldset, true);
    });

    // 2) Red de seguridad: si por lo que sea se intenta enviar el
    //    formulario de compra sin talla confirmada, lo bloqueamos aquí
    //    (fase de captura, antes de que llegue al handler nativo de Shopify).
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;

      var formId = form.getAttribute('id') || '';
      var sectionId = formId.replace(/^product-form-/, '');
      if (!sectionId || sectionId === formId) return;

      var fieldset = document.querySelector('.tf-size-fieldset[data-tf-section-id="' + sectionId + '"]');
      if (!fieldset) return; // este producto no tiene selector de talla

      if (!TF_SIZE_STATE[sectionId]) {
        e.preventDefault();
        e.stopImmediatePropagation();

        var error = fieldset.nextElementSibling;
        if (error && error.hasAttribute('data-tf-size-error')) {
          error.hidden = false;
        }

        fieldset.classList.remove('tf-size-shake');
        void fieldset.offsetWidth; // reinicia la animación si se repite
        fieldset.classList.add('tf-size-shake');

        try {
          fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (err) { /* no-op */ }
      }
    }, true);

    // 3) Shopify vuelve a renderizar el picker por AJAX cada vez que cambia
    //    cualquier opción (p. ej. color). Reaplicamos nuestro estado tras
    //    cada uno de esos renders para no perder el "confirmado".
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
        var sectionId = evt && evt.data && evt.data.sectionId;
        if (!sectionId) {
          tfSizeInit();
          return;
        }
        var fieldset = document.querySelector('.tf-size-fieldset[data-tf-section-id="' + sectionId + '"]');
        if (fieldset) {
          if (tfSizeHasSingleAvailableOption(fieldset)) {
            TF_SIZE_STATE[sectionId] = true;
          }
          tfSizeApplyState(fieldset, !!TF_SIZE_STATE[sectionId]);
        }
      });
    }
  }

  function tfSizeBoot() {
    tfSizeWireEvents();
    tfSizeInit();
  }

  function boot() {
    try { wirePersonalization(); } catch (e) { console.error('[TwoFive PDP]', e); }
    try { tfSizeBoot(); } catch (e) { console.error('[TwoFive PDP]', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
  document.addEventListener('shopify:block:select', boot);
})();